import React from "react";
import { twMerge } from "tailwind-merge";
import { CustomButton } from "./CustomButton";
import { HiOutlineUser } from "react-icons/hi";
import { LinkItem } from "./LinkManager";
import { useTranslation } from "react-i18next";
import { VCardUtils } from "../../utils/vCardUtils";
import { useCardPreview } from "../../contexts/CardPreviewContext";

interface VCardData {
  firstName: string;
  lastName: string;
  jobTitle?: string;
  email: string;
  phone?: string;
  description?: string;
  company?: string;
  website?: string;
  links?: LinkItem[];
  avatar?: string;
}

interface VCardGeneratorProps {
  data: VCardData;
  buttonColor?: string;
  className?: string;
}

const ACCENTS: Record<string, string> = {
  blue: "bg-blue-600 text-white enabled:hover:bg-blue-700",
  red: "bg-gradient-to-r from-red-600 to-red-700 text-white enabled:hover:from-red-700 enabled:hover:to-red-800",
  amber:
    "bg-gradient-to-r from-amber-500 to-amber-600 text-white enabled:hover:from-amber-600 enabled:hover:to-amber-700",
  purple:
    "bg-gradient-to-r from-purple-500 to-pink-500 text-white enabled:hover:from-purple-600 enabled:hover:to-pink-600",
  rose: "bg-gradient-to-r from-rose-500 to-pink-500 text-white enabled:hover:from-rose-600 enabled:hover:to-pink-600",
  pink: "bg-gradient-to-r from-pink-500 to-rose-500 text-white enabled:hover:from-pink-600 enabled:hover:to-rose-600",
  teal: "bg-gradient-to-r from-teal-500 to-emerald-500 text-white enabled:hover:from-teal-600 enabled:hover:to-emerald-600",
  cyan: "bg-gradient-to-r from-blue-600 to-cyan-600 text-white enabled:hover:from-blue-500 enabled:hover:to-cyan-500",
  slate: "bg-gray-800 text-white enabled:hover:bg-gray-900",
  gray: "bg-gray-800 text-white enabled:hover:bg-gray-900",
  light:
    "bg-white text-gray-900 border border-gray-300 enabled:hover:bg-gray-50",
};

const VCardGenerator: React.FC<VCardGeneratorProps> = ({
  data,
  buttonColor = "gray",
  className = "",
}) => {
  const { t } = useTranslation("clientView");
  const isPreview = useCardPreview();

  const decodeHtmlEntities = (text: string = ""): string => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const generateVCard = () => {
    const website =
      data.website ||
      data.links?.find(
        (link) =>
          link.link.includes("http") &&
          !link.link.includes("linkedin") &&
          !link.link.includes("facebook")
      )?.link;

    const linkedin = data.links?.find((link) =>
      link.link.toLowerCase().includes("linkedin")
    )?.link;

    try {
      VCardUtils.downloadVCard({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        jobTitle: data.jobTitle,
        companyName: data.company,
        avatarUrl: data.avatar,
        websiteUrl: website,
        urls: linkedin
          ? [
              {
                value: linkedin,
                type: "LinkedIn",
              },
            ]
          : undefined,
        note: decodeHtmlEntities(data.description),
        generatorLabel: "Professional excellence with Super-User-Manager.com",
      });
    } catch (error) {
      console.error("Error generating vCard:", error);
    }
  };

  return (
    <CustomButton
      variant="custom"
      className={twMerge(ACCENTS[buttonColor] ?? ACCENTS.gray, className)}
      onClick={isPreview ? undefined : generateVCard}
      disabled={isPreview}
      tabIndex={isPreview ? -1 : undefined}
      aria-hidden={isPreview ? true : undefined}
      comp={
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center justify-items-center">
          <HiOutlineUser size={16} />
          <div className="truncate text-xs">{t("key85")}</div>
        </div>
      }
    />
  );
};

export default VCardGenerator;
